// clubs.js - which club the app is wearing right now.
//
// SeasonCurve is the product; a club is what you see once the app knows who
// you are. The front door is deliberately neutral: someone arriving at
// seasoncurve.com has not told us anything yet, so showing one club's crest
// would be wrong for everyone else holding a code.
//
// Pure data and pure functions, so the resolution is unit-testable without a
// DOM. applyClubTheme() is the only part that touches the document.
//
// NOT a tenancy model. This decides what the app LOOKS like, never what anyone
// can reach: that stays with the JWT and the row-level policies. Keeping the
// two apart matters, because a club theme is chosen from a code typed by an
// anonymous visitor and must not be able to grant anything.

export const NEUTRAL = {
  id:    'seasoncurve',
  name:  'SeasonCurve',
  logo:  '/assets/brand/seasoncurve/logo.svg',
  mark:  '/assets/brand/seasoncurve/mark.svg',
  // Ink and cyan from the brand system.
  vars: {
    '--club-ink':    '#201E1D',
    '--club-accent': '#0088B0',
  },
};

export const CLUBS = {
  dragons: {
    id:        'dragons',
    name:      'KHC Dragons',
    tagline:   'Evaluation & Feedback Manager',
    crest:     '/assets/logo.png',
    crestDark: '/assets/logo.png',       // already reads on navy
    scheme:    'dark',
    // The app's existing palette. Named for what they are historically; the
    // whole stylesheet already reads these, so a club swap is a variable swap.
    vars: {},                     // dark navy is the stylesheet default
  },
  victory: {
    id:        'victory',
    name:      'Royal Victory H.C.',
    tagline:   'Est. 1919',
    crest:     '/assets/brand/victory/crest.png',
    // The positive crest is black line art and disappears on a dark panel, so
    // anything dark (the front door) must use the negative treatment. The
    // design system ships both for exactly this reason.
    crestDark: '/assets/brand/victory/crest-neg.png',
    scheme:    'light',
    // Royal Victory's own tokens: red on white and paper, not navy on dark.
    vars: {
      '--club-ink':      '#1C1C1B',
      '--club-accent':   '#DF0C38',
      '--club-accent-2': '#B80A2E',
      '--club-paper':    '#F8F6F4',
      '--club-surface':  '#FFFFFF',
      '--club-muted':    '#6B7076',
      '--club-border':   '#D9DBDC',
    },
  },
  // A club with no real person in it, for working on the app safely. Every
  // director policy is club-scoped, so its code reaches nothing else; seeded
  // by supabase/migrations/20261029150000_seed_test_club.sql. Its players are
  // named "Speler Een" and so on, which is the point: nothing here can be
  // mistaken for a child.
  testclub: {
    id:        'testclub',
    name:      'Seasoncurve Test Club',
    tagline:   'Test data only',
    crest:     '/assets/logo.png',
    crestDark: '/assets/logo.png',
    scheme:    'dark',
    vars: {},
  },
};

/**
 * Codes that belong to a club other than the default.
 *
 * Prefix matching rather than a list of every code, so adding a Victory team
 * is a code that starts with Vic rather than a change here. Checked
 * case-insensitively because people type codes off paper.
 */
const PREFIXES = [
  { prefix: 'VIC', club: 'victory' },
  { prefix: 'DIR-TESTCLUB', club: 'testclub' },
];

export const DEFAULT_CLUB = 'dragons';

/**
 * The club whose data this session is looking at.
 *
 * Kept here rather than in the session because db.js needs it on every list
 * query and must not depend on auth. Set when a club is worn; read by the
 * handful of "everything" queries so all thirty of their call sites stay
 * correct without each one having to remember.
 *
 * Partitioning, not permission: it decides which rows are WORTH asking for,
 * never which rows a person may see. That stays with the JWT and the policies.
 */
let _activeClub = DEFAULT_CLUB;
export const activeClubId = () => _activeClub;
export function setActiveClub(id) { _activeClub = (id && CLUBS[id]) ? id : DEFAULT_CLUB; }

/**
 * Which club this session belongs to.
 *
 * Prefers the club the SERVER put in the session, because every login now
 * stamps club_id into the signed token and that is the authoritative answer.
 * The code spelling is only a fallback for the one moment a session does not
 * exist yet: a coach's code is validated before she has typed her name.
 *
 * Reading the club off the prefix alone was a trap. It meant a club's director
 * code had to START with that club's letters or the app would paint itself the
 * wrong colours AND filter its lists to the wrong club, showing nothing, with
 * no hint that the name of the code was the reason.
 *
 * @param {object} session  the stored session, or null
 * @param {string} code     the code that was typed
 */
export function clubForSession(session, code) {
  const id = session && session.clubId;
  if (id && CLUBS[id]) return CLUBS[id];
  return clubForCode(code);
}

/** Which club a typed code belongs to. Unknown or empty = the default club. */
export function clubIdForCode(code) {
  const c = String(code || '').trim().toUpperCase();
  if (!c) return DEFAULT_CLUB;
  const hit = PREFIXES.find(p => c.startsWith(p.prefix));
  return hit ? hit.club : DEFAULT_CLUB;
}

/** The club record for a code, always something renderable. */
export function clubForCode(code) {
  return CLUBS[clubIdForCode(code)] || CLUBS[DEFAULT_CLUB];
}

/** The crest to use on a dark panel, falling back to the ordinary one. */
export function crestForDark(club) {
  const c = club || CLUBS[DEFAULT_CLUB];
  return c.crestDark || c.crest;
}

export function clubById(id) {
  return CLUBS[id] || CLUBS[DEFAULT_CLUB];
}

/**
 * Wear a club. Sets data-club for CSS to hook, and writes the club's variables
 * onto the root so existing rules pick them up without being rewritten.
 *
 * Variables from a previous club are cleared first. Without that, switching
 * from Victory back to Dragons would leave Victory's red behind on anything
 * Dragons does not itself define, which is the sort of bug that only shows up
 * on the second login of a session.
 */
let _applied = [];
export function applyClubTheme(club, root = (typeof document !== 'undefined' ? document.documentElement : null)) {
  if (!root) return;
  _applied.forEach(k => root.style.removeProperty(k));
  _applied = [];
  const c = club || CLUBS[DEFAULT_CLUB];
  setActiveClub(c.id);
  root.setAttribute('data-club', c.id);
  root.setAttribute('data-scheme', c.scheme || 'dark');
  Object.entries(c.vars || {}).forEach(([k, v]) => {
    root.style.setProperty(k, v);
    _applied.push(k);
  });

  // The club's NAME, not just its colours. Without this the home screen says
  // "KHC Dragons" to every club, because the eyebrow was written into the
  // markup before there was a second one. Any element marked [data-club-name]
  // follows the club; [data-club-tagline] likewise.
  const doc = root.ownerDocument || (typeof document !== 'undefined' ? document : null);
  if (!doc) return;
  doc.querySelectorAll('[data-club-name]').forEach(el => { el.textContent = c.name; });
  doc.querySelectorAll('[data-club-tagline]').forEach(el => { el.textContent = c.tagline || ''; });
}

/**
 * Mark the page as a developer's support session, or clear the mark.
 *
 * A support session is a director to the database by design, which is exactly
 * why it must never LOOK like one. Without this the only difference between
 * being in your own club and being inside somebody else's is a line in a log
 * nobody is reading at the time.
 *
 * A bar rather than a badge, because a badge is something you stop noticing.
 */
export function markDeveloperSession(label, clubName,
                                     doc = (typeof document !== 'undefined' ? document : null)) {
  if (!doc) return;
  const existing = doc.getElementById('developer-bar');
  if (!label) { existing?.remove(); doc.body?.classList.remove('is-developer'); return; }
  const bar = existing || doc.createElement('div');
  bar.id = 'developer-bar';
  bar.className = 'developer-bar';
  bar.textContent = `Developer access as ${label} \u00b7 ${clubName || 'this club'} \u00b7 recorded`;
  if (!existing) doc.body?.insertBefore(bar, doc.body.firstChild);
  doc.body?.classList.add('is-developer');
}

/** Back to the neutral product brand, for the front door and after sign-out. */
export function applyNeutralTheme(root = (typeof document !== 'undefined' ? document.documentElement : null)) {
  if (!root) return;
  _applied.forEach(k => root.style.removeProperty(k));
  _applied = [];
  root.setAttribute('data-club', NEUTRAL.id);
  root.removeAttribute('data-scheme');
  Object.entries(NEUTRAL.vars).forEach(([k, v]) => {
    root.style.setProperty(k, v);
    _applied.push(k);
  });
}
